<template>
    <div class="manager-assignment">
        <el-card class="box-card">
            <!-- 学校选择 -->
            <el-form :inline="true" size="small" class="search-form">
                <el-form-item label="选择学校">
                    <el-select 
                        v-model="selectedSchool" 
                        filterable 
                        :remote="!schoolLocked"
                        placeholder="请选择学校"
                        :remote-method="searchSchools" 
                        :loading="schoolLoading"
                        :disabled="schoolLocked"
                        :clearable="!schoolLocked"
                        @change="onSchoolChange">
                        <el-option
                            v-for="item in schoolOptions"
                            :key="item.schoolId"
                            :label="item.schoolName"
                            :value="item.schoolId">
                        </el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="筛选负责人">
                    <el-select 
                        v-model="filterManager" 
                        filterable 
                        clearable
                        placeholder="选择负责人筛选兼职"
                        @change="loadRunners">
                        <el-option label="全部兼职" :value="null"></el-option>
                        <el-option label="自主兼职" :value="0"></el-option>
                        <el-option
                            v-for="item in managerList"
                            :key="item.campusUserId"
                            :label="item.jobName"
                            :value="item.campusUserId">
                        </el-option>
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button 
                        type="primary" 
                        icon="el-icon-refresh"
                        @click="loadData">刷新数据</el-button>
                </el-form-item>
            </el-form>

            <el-divider></el-divider>

            <!-- 批量操作 -->
            <div class="batch-actions" v-if="selectedSchool">
                <el-button 
                    type="success" 
                    icon="el-icon-edit"
                    size="small"
                    :disabled="selectedRunners.length === 0"
                    @click="showBatchAssignDialog">
                    批量分配负责人 ({{ selectedRunners.length }})
                </el-button>
                <el-button 
                    type="warning" 
                    icon="el-icon-remove-outline"
                    size="small"
                    :disabled="selectedRunners.length === 0"
                    @click="batchRemoveManager">
                    批量移除负责人 ({{ selectedRunners.length }})
                </el-button>
            </div>

            <!-- 兼职列表 -->
            <el-table
                v-loading="loading"
                :data="runnerList"
                border
                style="width: 100%; margin-top: 20px"
                @selection-change="handleSelectionChange"
                max-height="600">
                <el-table-column
                    type="selection"
                    width="55">
                </el-table-column>
                <el-table-column prop="jpPortrait" label="头像" width="80" align="center">
                    <template slot-scope="scope">
                        <el-avatar :size="40" :src="scope.row.jpPortrait" />
                    </template>
                </el-table-column>
                <el-table-column prop="jpNickName" label="昵称" width="120" show-overflow-tooltip>
                </el-table-column>
                <el-table-column prop="jobName" label="真实姓名" width="100">
                </el-table-column>
                <el-table-column prop="jobPhone" label="手机号" width="120">
                </el-table-column>
                <el-table-column prop="schoolName" label="学校" min-width="150" show-overflow-tooltip>
                </el-table-column>
                <el-table-column prop="managerName" label="当前负责人" width="120">
                    <template slot-scope="scope">
                        <el-tag 
                            v-if="scope.row.managerName === '自主兼职'" 
                            type="info" 
                            size="small">
                            {{ scope.row.managerName }}
                        </el-tag>
                        <el-tag 
                            v-else 
                            type="success" 
                            size="small">
                            {{ scope.row.managerName }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="createTime" label="注册时间" width="170" sortable>
                    <template slot-scope="scope">
                        <i class="el-icon-time"></i>
                        <span style="margin-left: 5px">{{ formatDate(scope.row.createTime) }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="200" align="center" fixed="right">
                    <template slot-scope="scope">
                        <el-button 
                            type="primary" 
                            icon="el-icon-edit"
                            size="mini"
                            @click="showAssignDialog(scope.row)">
                            分配负责人
                        </el-button>
                        <el-button 
                            v-if="scope.row.jobParentId > 0"
                            type="warning" 
                            icon="el-icon-remove"
                            size="mini"
                            @click="removeManager(scope.row)">
                            移除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- 分配负责人对话框 -->
        <el-dialog 
            title="分配负责人" 
            :visible.sync="assignDialogVisible" 
            width="500px"
            @close="resetAssignForm">
            <el-form :model="assignForm" label-width="100px">
                <el-form-item label="兼职姓名">
                    <span>{{ assignForm.runnerName }}</span>
                </el-form-item>
                <el-form-item label="当前负责人">
                    <span>{{ assignForm.currentManager }}</span>
                </el-form-item>
                <el-form-item label="新负责人" required>
                    <el-select 
                        v-model="assignForm.newManagerId" 
                        filterable 
                        placeholder="请选择负责人">
                        <el-option label="自主兼职(无负责人)" :value="0"></el-option>
                        <el-option
                            v-for="item in managerList"
                            :key="item.campusUserId"
                            :label="item.jobName"
                            :value="item.campusUserId">
                        </el-option>
                    </el-select>
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="assignDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmAssign">确定</el-button>
            </span>
        </el-dialog>

        <!-- 批量分配对话框 -->
        <el-dialog 
            title="批量分配负责人" 
            :visible.sync="batchAssignDialogVisible" 
            width="500px"
            @close="resetBatchForm">
            <el-alert
                title="提示"
                type="info"
                :closable="false"
                style="margin-bottom: 20px">
                已选择 {{ selectedRunners.length }} 个兼职
            </el-alert>
            <el-form :model="batchForm" label-width="100px">
                <el-form-item label="分配给" required>
                    <el-select 
                        v-model="batchForm.managerId" 
                        filterable 
                        placeholder="请选择负责人">
                        <el-option
                            v-for="item in managerList"
                            :key="item.campusUserId"
                            :label="item.jobName"
                            :value="item.campusUserId">
                        </el-option>
                    </el-select>
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="batchAssignDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmBatchAssign">确定</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { isSchoolScoped, getBoundSchoolId, getBoundSchoolName } from '@/utils/schoolScope'
export default {
    name: 'ManagerAssignment',
    computed: {
        schoolLocked() {
            return isSchoolScoped();
        }
    },
    data() {
        return {
            selectedSchool: null,
            schoolOptions: [],
            schoolLoading: false,
            filterManager: null,
            
            managerList: [],
            runnerList: [],
            loading: false,
            
            selectedRunners: [],
            
            // 单个分配
            assignDialogVisible: false,
            assignForm: {
                runnerId: null,
                runnerName: '',
                currentManager: '',
                newManagerId: null
            },
            
            // 批量分配
            batchAssignDialogVisible: false,
            batchForm: {
                managerId: null
            }
        };
    },
    mounted() {
        if (isSchoolScoped() && getBoundSchoolId() != null) {
            this.schoolOptions = [{
                schoolId: getBoundSchoolId(),
                schoolName: getBoundSchoolName()
            }];
            this.selectedSchool = getBoundSchoolId();
            this.loadData();
        }
    },
    methods: {
        // 搜索学校
        async searchSchools(query) {
            if (query !== '') {
                this.schoolLoading = true;
                try {
                    const { data: res } = await this.$http.get('/school/getSchoolBySchoolName', {
                        params: { schoolName: query }
                    });
                    this.schoolOptions = res.data || [];
                } catch (error) {
                    this.$message.error('搜索学校失败');
                } finally {
                    this.schoolLoading = false;
                }
            } else {
                this.schoolOptions = [];
            }
        },
        
        // 学校变化时
        onSchoolChange() {
            this.filterManager = null;
            this.loadData();
        },
        
        // 加载数据
        async loadData() {
            if (!this.selectedSchool) {
                this.$message.warning('请先选择学校');
                return;
            }
            
            await Promise.all([
                this.loadManagers(),
                this.loadRunners()
            ]);
        },
        
        // 加载负责人列表
        async loadManagers() {
            try {
                const { data: res } = await this.$http.get('/campusjob/getManagersBySchool', {
                    params: { schoolId: this.selectedSchool }
                });
                if (res.code === 200) {
                    this.managerList = res.data || [];
                } else {
                    this.$message.error('获取负责人列表失败');
                }
            } catch (error) {
                this.$message.error('获取负责人列表失败');
            }
        },
        
        // 加载兼职列表
        async loadRunners() {
            if (!this.selectedSchool) return;
            
            this.loading = true;
            try {
                const params = {
                    schoolId: this.selectedSchool
                };
                if (this.filterManager !== null) {
                    params.managerId = this.filterManager;
                }
                
                const { data: res } = await this.$http.get('/campusjob/getRunnersBySchool', {
                    params: params
                });
                if (res.code === 200) {
                    this.runnerList = res.data || [];
                } else {
                    this.$message.error('获取兼职列表失败');
                }
            } catch (error) {
                this.$message.error('获取兼职列表失败');
            } finally {
                this.loading = false;
            }
        },
        
        // 选择变化
        handleSelectionChange(val) {
            this.selectedRunners = val;
        },
        
        // 显示分配对话框
        showAssignDialog(row) {
            this.assignForm = {
                runnerId: row.id,
                runnerName: row.jobName,
                currentManager: row.managerName,
                newManagerId: row.managerId || 0
            };
            this.assignDialogVisible = true;
        },
        
        // 确认分配
        async confirmAssign() {
            if (this.assignForm.newManagerId === null) {
                this.$message.warning('请选择负责人');
                return;
            }
            
            try {
                const { data: res } = await this.$http.post('/campusjob/assignManager', null, {
                    params: {
                        runnerId: this.assignForm.runnerId,
                        managerId: this.assignForm.newManagerId
                    }
                });
                
                if (res.code === 200) {
                    this.$message.success('分配成功');
                    this.assignDialogVisible = false;
                    this.loadRunners();
                } else {
                    this.$message.error(res.msg || '分配失败');
                }
            } catch (error) {
                this.$message.error('分配失败');
            }
        },
        
        // 移除负责人
        removeManager(row) {
            this.$confirm(`确定将 ${row.jobName} 从 ${row.managerName} 的团队中移除吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const { data: res } = await this.$http.post('/campusjob/assignManager', null, {
                        params: {
                            runnerId: row.id,
                            managerId: 0
                        }
                    });
                    
                    if (res.code === 200) {
                        this.$message.success('移除成功');
                        this.loadRunners();
                    } else {
                        this.$message.error(res.msg || '移除失败');
                    }
                } catch (error) {
                    this.$message.error('移除失败');
                }
            }).catch(() => {});
        },
        
        // 显示批量分配对话框
        showBatchAssignDialog() {
            this.batchAssignDialogVisible = true;
        },
        
        // 确认批量分配
        async confirmBatchAssign() {
            if (!this.batchForm.managerId) {
                this.$message.warning('请选择负责人');
                return;
            }
            
            const runnerIds = this.selectedRunners.map(r => r.id);
            
            try {
                const { data: res } = await this.$http.post('/campusjob/batchAssignManager', {
                    runnerIds: runnerIds,
                    managerId: this.batchForm.managerId
                });
                
                if (res.code === 200) {
                    const result = res.data;
                    this.$message.success(`成功分配 ${result.successCount} 个兼职`);
                    this.batchAssignDialogVisible = false;
                    this.loadRunners();
                } else {
                    this.$message.error(res.msg || '批量分配失败');
                }
            } catch (error) {
                this.$message.error('批量分配失败');
            }
        },
        
        // 批量移除负责人
        batchRemoveManager() {
            this.$confirm(`确定将选中的 ${this.selectedRunners.length} 个兼职移除负责人吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                const runnerIds = this.selectedRunners.map(r => r.id);
                
                try {
                    const { data: res } = await this.$http.post('/campusjob/batchAssignManager', {
                        runnerIds: runnerIds,
                        managerId: 0
                    });
                    
                    if (res.code === 200) {
                        const result = res.data;
                        this.$message.success(`成功移除 ${result.successCount} 个兼职的负责人`);
                        this.loadRunners();
                    } else {
                        this.$message.error(res.msg || '批量移除失败');
                    }
                } catch (error) {
                    this.$message.error('批量移除失败');
                }
            }).catch(() => {});
        },
        
        // 重置表单
        resetAssignForm() {
            this.assignForm = {
                runnerId: null,
                runnerName: '',
                currentManager: '',
                newManagerId: null
            };
        },
        
        resetBatchForm() {
            this.batchForm = {
                managerId: null
            };
        },
        
        // 格式化日期
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        }
    }
};
</script>

<style scoped>
.manager-assignment {
    padding: 20px;
}

.box-card {
    width: 100%;
}

.search-form {
    margin-bottom: 10px;
}

.batch-actions {
    margin-bottom: 15px;
}
</style>

